import _ from "underscore";
import moment from "moment";

import Backbone from "pages/common/lib/backbone-helpers";
import { isGrn } from "util/grn-utils";
import Constants from "const/constants";
import { publicRuntimeConfig } from "util/env-utils";

import DVRBlockCollection from "pages/trimmer/state/collection/dvrblocks";
import ProducerElasticEncoder from "pages/producer/common/lib/ProducerElasticEncoder";

const StreamModel = Backbone.Model.extend({
    idAttribute: `streamId`,

    toFormFormat: function () {
        var data = {
            id: this.id,
            displayName: this.get(`displayName`),
            organizationId: this.get(`organizationId`),
            epgEnabled: this.get(`isEpgEnabled`),
            hasListeners: this.get(`hasListeners`)
        };

        if (this.get(`coverImage`) !== this.defaults.coverImage) {
            data.coverImage = this.get(`coverImage`);
        }

        return data;
    },

    url: function () {
        return `${Constants.DEFAULT_API_PATH}/streams/${this.id}`;
    },

    dvrBlocksUrl: function () {
        const req = _.clone(this.trimmerOptions);
        if (req.endTimestamp === Infinity) {
            delete req.endTimestamp;
        }
        return `${publicRuntimeConfig.UDVR_URL}/api/v2/dvr/contentmap?${$.param(
            _.extend(
                {
                    streamId: this.id,
                    allowDiscontinuity: false
                },
                req
            )
        )}`;
    },

    constants: {
        EXTENSION: {
            html5: `m3u8`
        },
        oneMinute: 60 * 1000
    },

    defaults: {
        coverImage: `/img/cards-stream.png`,
        streamSelection: `primary`,
        dvrStoreRetentionTime: 24 * 60 * 60 * 1000, // one day
        epgEnabled: false,
        hasListeners: false
    },

    regex: {
        streamUrlNoSpace: /^(http(s)?|rtmp(e)?):\/\/(\S*$)/,
        streamUrlIpAddress:
            /^(http(s)?|rtmp(e)?):\/\/\d{1,3}(\.\d{1,3}){3}(:(\d){1,5})?\//,
        // eslint-disable-next-line
        streamUrlDomainName:
            /^(http(s)?|rtmp(e)?):\/\/[\w\.-]+(\.[a-zA-Z]{2,})(:\d+)?\//
    },

    initialize: function () {
        const streamType = isGrn(this.get(`encodeSource`))
            ? Constants.STREAM_TYPE.PUSH_STREAM
            : Constants.STREAM_TYPE.PULL_STREAM;

        this.set({ streamType });

        this.on(
            `change:exportedEncodeRecords`,
            this.updateSocialCredentialExportedEncodes,
            this
        );

        _.bindAll(this, `fetch`, `onDvrBlocksFetched`, `onDvrBlocksFail`);
    },

    parse: function (resp) {
        const data = resp.data || resp;

        if (this && this.attributes) {
            for (var key in this.attributes) {
                if (key !== `streamType` && data[key] === undefined) {
                    data[key] = undefined;
                }
            }
        }

        return data;
    },

    select: function (options) {
        options = _.isObject(options) ? options : {};
        this.trimmerOptions = options;

        this.getBlocks();
    },

    getBlocks: function () {
        this.failedDvrFetch = false;
        const $promise = $.get({
            url: this.dvrBlocksUrl(),
            xhrFields: { withCredentials: true }
        })
            .done(this.onDvrBlocksFetched)
            .fail(this.onDvrBlocksFail);
        return $promise;
    },

    onDvrBlocksFetched: function (blocks) {
        this.blocks = new DVRBlockCollection(blocks.contentSections, {
            parse: true,
            startTimestamp: this.trimmerOptions.startTimestamp,
            endTimestamp: this.trimmerOptions.endTimestamp
        });

        App.Events.trigger(`blocks:ready`);
    },

    onDvrBlocksFail: function () {
        if (!this.failedDvrFetch) {
            App.Events.trigger(`contentmap:fail`);
        }
    },

    startFetching: function () {
        this.fetch({
            reset: true
        });
        this.fetchIntervalID = setInterval(
            () =>
                this.fetch({
                    reset: true
                }),
            10000
        );
    },

    stopFetching: function () {
        clearInterval(this.fetchIntervalID);
    },

    updateEncodeSource: function (encodeSource) {
        return $.ajax({
            url: `${Constants.DEFAULT_API_PATH}/streams/${this.id}/encode/source`,
            method: `PUT`,
            contentType: `application/json`,
            processData: false,
            data: { encodeSource }
        }).done(({ data }) => {
            this.set(`encodeSource`, data.encodeSource);
            this.set(`sourceUrl`, data.sourceUrl);
            this.set(`sourceHlsUrl`, data.sourceHlsUrl);
        });
    },

    updateSocialCredentialExportedEncodes: function () {
        App.Models.user.socialCredentials.updateExportedEncodeRecords(
            this.get(`exportedEncodeRecords`)
        );
    },

    generateQueryParam: function () {
        var params = {};

        if (window.App.Utils.params.channelId) {
            params.channelId = window.App.Utils.params.channelId;
        } else if (window.App.Utils.params.brandId) {
            params.brandId = window.App.Utils.params.brandId;
        } else if (window.App.Utils.params.eventId) {
            params.eventId = window.App.Utils.params.eventId;
        } else if (window.App.Utils.params.galleryId) {
            params.campaignId = window.App.Utils.params.galleryId;
        } else if (window.App.Utils.params.campaignId) {
            params.campaignId = window.App.Utils.params.campaignId;
        }

        params[this.idAttribute] = this.id;
        return $.param(params);
    },

    getName: function () {
        if (!_.isUndefined(this.get(`primaryStreamName`))) {
            return this.get(`primaryStreamName`);
        } else {
            return this.get(`streamId`);
        }
    },

    getDisplayName: function () {
        // Returns displayName if exists
        // fallback to streamName otherwise
        // and fallback to streamId if streamName is missing
        if (!_.isUndefined(this.get(`displayName`))) {
            return this.get(`displayName`);
        } else if (!_.isUndefined(this.get(`primaryStreamName`))) {
            return this.get(`primaryStreamName`);
        } else {
            return this.get(`streamId`);
        }
    },

    isBroadcasting: function () {
        var exportedEncodeRecords = this.get(`exportedEncodeRecords`);
        if (_.isArray(exportedEncodeRecords)) {
            var liveEncodeRecords = _.where(exportedEncodeRecords, {
                status: `live`
            });
            if (liveEncodeRecords.length > 0) {
                return true;
            }
        }
        return false;
    },

    getBadgeType: function () {
        return this.get(`streamStatus`) === `online` ? `live` : `none`;
    },

    getStreamUrl: function (playerType, startTime, endTime) {
        var now = new Date().getTime();

        var extension = this.constants.EXTENSION[playerType];

        var queryString = {
            DVR: true,
            startDate: startTime
        };
        if (_.isNumber(endTime) && endTime < now) {
            queryString.endDate = endTime;
        }

        var streamSelection = this.get(`streamSelection`);
        var streamServer = this.get(
            prefixModelKey(streamSelection, `streamServer`)
        );

        var dvrStoreName = this.getDvrStoreName(endTime);
        this.set(`dvrStoreName`, dvrStoreName);

        return `${streamServer + dvrStoreName}/manifest.${extension}?${$.param(
            queryString
        )}`;
    },

    getAttr: function (key) {
        if (this.has(key)) {
            return StreamModel.__super__.get.apply(this, arguments);
        } else {
            var streamSelection = this.has(`streamSelection`)
                ? this.get(`streamSelection`)
                : `primary`;
            var prefixedKey = prefixModelKey(streamSelection, key);
            return StreamModel.__super__.get.call(this, prefixedKey);
        }
    },

    getDvrStreamQueryURL: function () {
        var streamServer = this.getAttr(`streamServer`);
        var delimiter = `.com`;
        var indexOfDotCom = streamServer.indexOf(delimiter);
        return `${streamServer.substring(
            0,
            indexOfDotCom + delimiter.length
        )}:8086/dvrstreamquery`;
    },

    getDvrStoreName: function (time) {
        var now = new Date().getTime();
        time = !_.isNumber(time) || time > now ? now : time;

        var streamName = this.getAttr(`streamName`);
        var dvrStoreRollingTimeOffset = this.getAttr(
            `dvrStoreRollingTimeOffset`
        );
        var dvrDate = moment
            .unix((time - dvrStoreRollingTimeOffset) / 1000)
            .utc()
            .format(`_YYYY_MM_DD`);

        return streamName + dvrDate;
    },

    getDvrStoreRetentionTime: function () {
        // Always prefer display retention time, fall back to actual
        return this.has(`displayedDvrStoreRetentionTime`)
            ? this.get(`displayedDvrStoreRetentionTime`)
            : this.get(`dvrStoreRetentionTime`);
    },

    getStreamStatus: function () {
        return this.get(`streamStatus`) !== ``
            ? this.get(`streamStatus`)
            : `offline`;
    },

    getDisableStreamShare: function () {
        return !this.isBroadcasting() && this.get(`streamStatus`) !== `online`;
    },

    isCloudProducerEnabled: function () {
        return !_.isUndefined(this.get(`producerEncodeId`));
    },

    isEnabled: function () {
        return (
            !_.isUndefined(this.get(`producerEncode`)) &&
            !!this.get(`producerEncode`).isEnabled
        );
    },

    getFrameRate: function () {
        var encodingPresets = this.get(`mbrEncodingPresets`);
        if (
            _.isObject(encodingPresets) &&
            Object.prototype.hasOwnProperty.call(encodingPresets, `original`)
        ) {
            return encodingPresets.original.videoFramesPerSecond;
        } else {
            return 25;
        }
    },

    getFrameDuration: function () {
        return 1000 / this.getFrameRate();
    },

    getFrameDurationInSeconds: function () {
        return 1 / this.getFrameRate();
    },

    startAdBreakCountdown(duration) {
        this.set(`overlayTime`, duration);
        // stop fetching to avoid overlayTime in stream model been reseted
        this.stopFetching();
        this.resfreshCountdown(duration);
    },

    resfreshCountdown(duration) {
        setTimeout(() => {
            duration--;
            this.set(`overlayTime`, duration);
            if (duration !== 0) {
                this.resfreshCountdown(duration);
            } else {
                this.startFetching(); // start fetching again
            }
        }, 1000);
    },

    toApiFormat() {
        const { streamId } = this.attributes;
        return {
            streamId
        };
    },

    isProducerEncode() {
        return (
            !_.isUndefined(this.get(`producerEncode`)) &&
            this.get(`producerEncode`) !== null
        );
    },

    getEncode() {
        return this.get(`producerEncode`);
    },

    isScalingEnabled: function () {
        const { dedicatedEncoder } = this.get(`producerEncode`) || {};
        return !_.isUndefined(dedicatedEncoder) && dedicatedEncoder === false;
    },

    setFailedDvrFetch() {
        this.set(`failedDvrFetch`, true);
    }
});

export default StreamModel;

const prefixModelKey = function (prefix, key) {
    prefix = !_.isUndefined(prefix) ? prefix : `primary`;
    return prefix + key.charAt(0).toUpperCase() + key.slice(1);
};

export const encoderFactory = function (streamModel = {}) {
    if (
        !_.isUndefined(streamModel.get(`producerEncodeId`)) &&
        !_.isUndefined(streamModel.get(`producerEncode`))
    ) {
        return new ProducerElasticEncoder({
            streamId: streamModel.id,
            encodeId: streamModel.get(`producerEncodeId`)
        });
    }
};
